// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { FhirEndpoint } from '../../../shared-api/fhirEndpoint';
import { FhirSearchResult } from '../../../shared-api/fhirSearchResult';
import { OrgAdminFhirEndpointsService } from '../../../shared-api/orgAdminFhirEndpoints.service';
import { VzdTokenService } from '../vzd-fhir/vzd-token.service';
import { FhirExtension } from '../../../shared-api/fhirExtension';
import { AppConfigService } from '../config/app-config.service';
import { OrgAdminFhirHealthcareServiceService } from '../../../shared-api/orgAdminFhirHealthcareService.service';
import { FhirOrganization } from '../../../shared-api/fhirOrganization';
import { FhirLocation } from '../../../shared-api/fhirLocation';
import { decodeJwt } from 'jose';
import { FhirHealthcareService } from '../../../shared-api/fhirHealthcareService';
import { FhirReference } from '../../../shared-api/fhirReference';
import { OrgAdminFhirOrganizationService } from '../../../shared-api/orgAdminFhirOrganization.service';
import { FhirEntry } from '../../../shared-api/fhirEntry';
import { HttpStatus, jsonError } from '../../errors/error-with-status';
import { sleep } from '../util';

@Injectable({
  providedIn: 'root'
})
/**
 * Der OrgAdminFhirEndpointsService stellt FHIR Endpoint-Management bereit.
 * Implementation of the mandatory OrgAdminFhirEndpoints API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class OrgAdminFhirSharedService
  implements OrgAdminFhirHealthcareServiceService, OrgAdminFhirEndpointsService, OrgAdminFhirOrganizationService
{
  private readonly vzdTokenService = inject(VzdTokenService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly vzdBase = this.appConfigService.get('vzdFhirUrl');

  /**
   * Delete healthcareService with this id
   * @param hsId ID of the healthcareService
   */
  public async deleteHealthcareService(hsId: string): Promise<object> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling deleteHealthcareService.');
    }

    const hcs = await this.getHealthcareService(hsId);

    if (hcs.endpoint) {
      for (const ep of hcs.endpoint) {
        await this.deleteEndpoint(hsId, ep.reference.split('/')[1]);
      }
    }

    const token = await this.vzdTokenService.getToken();

    const url = `${this.vzdBase}/owner/HealthcareService/${hsId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'DeleteHealthcareServiceError',
        response,
        'Löschen des Healthcareservices fehlgeschlagen'
      );
    }
    return {};
  }

  public async getOrganizationLocation(): Promise<{ organization: FhirOrganization; location: FhirLocation }> {
    const token = await this.vzdTokenService.getToken();
    const tid = decodeJwt(token)?.sub;

    if (!tid) {
      throw new Error('Es konnte keine Telematik ID gefunden werden');
    }

    const params = new URLSearchParams();
    params.append('_include', 'Organization:*');
    params.append('_include', 'Location:*');
    params.append('organization.identifier', tid);

    const url = `${this.vzdBase}/owner/HealthcareService?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'GetOrganizationLocationError',
        response,
        'Get Organisation und Location fehlgeschlagen'
      );
    }

    const responseBody = (await response.json()) as FhirSearchResult;

    if (!responseBody.entry) {
      throw new Error('Es konnte kein Eintrag für Organization oder Location gefunden werden');
    }
    const organization = responseBody.entry.find(entry => entry.resource.resourceType === 'Organization')
      ?.resource as FhirOrganization;
    const location = responseBody.entry.find(entry => entry.resource.resourceType === 'Location')
      ?.resource as FhirLocation;

    return { organization, location };
  }

  public async createHealthcareService(fhirHealthcareService?: FhirHealthcareService): Promise<FhirHealthcareService> {
    if (!fhirHealthcareService?.name) {
      throw new Error('Required parameter name was null or undefined when calling createHealthcareService.');
    }

    const token = await this.vzdTokenService.getToken();

    const { organization, location } = await this.getOrganizationLocation();

    fhirHealthcareService.providedBy = { reference: `Organization/${organization.id}` };
    fhirHealthcareService.location = [{ reference: `Location/${location.id}` }];
    fhirHealthcareService.availableTime ??= [];
    fhirHealthcareService.serviceProvisionCode ??= [];
    fhirHealthcareService.specialty ??= [];
    fhirHealthcareService.endpoint ??= [];
    fhirHealthcareService.meta ??= {
      profile: ['https://gematik.de/fhir/directory/StructureDefinition/HealthcareServiceDirectory'],
      tag: [
        {
          code: 'owner',
          system: 'https://gematik.de/fhir/directory/CodeSystem/Origin'
        }
      ]
    };

    const url = `${this.vzdBase}/owner/HealthcareService`;
    console.debug(`Sending POST request to ${url}, body=${JSON.stringify(fhirHealthcareService)}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fhirHealthcareService)
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'CreateHealthcareServiceError',
        response,
        `Anlegen des Healthcareservices fehlgeschlagen. requestBody=${JSON.stringify(fhirHealthcareService)}`
      );
    }

    return (await response.json()) as FhirHealthcareService;
  }

  /**
   * Changes healthcareService accordingly
   * @param hsId ID of the healthcareService
   * @param fhirHealthcareService
   */
  public async updateHealthcareService(
    hsId: string,
    fhirHealthcareService?: FhirHealthcareService
  ): Promise<FhirHealthcareService> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling updateHealthcareService.');
    }

    if (!fhirHealthcareService?.name) {
      throw new Error('Required parameter name was null or undefined when calling updateHealthcareService.');
    }

    if (!fhirHealthcareService.id) {
      fhirHealthcareService.id = hsId;
    }

    const token = await this.vzdTokenService.getToken();

    const url = `${this.vzdBase}/owner/HealthcareService/${hsId}`;
    const init: RequestInit = {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fhirHealthcareService)
    };

    const response = await this.retryFetch(url, init);

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'UpdateHealthcareServiceError',
        response,
        'Bearbeiten des Healthcareservices fehlgeschlagen'
      );
    }

    return (await response.json()) as FhirHealthcareService;
  }

  /**
   * Get healthcareService for this id
   * @param hsId ID of the healthcareService
   */
  public async getHealthcareService(hsId: string): Promise<FhirHealthcareService> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling getHealthcareService.');
    }

    const token = await this.vzdTokenService.getToken();

    const params = new URLSearchParams();
    params.append('_id', hsId);
    params.append('organization.active', 'true');

    const url = `${this.vzdBase}/search/HealthcareService?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'GetHealthcareServiceSearchError',
        response,
        'Suchen des Healthcareservices fehlgeschlagen'
      );
    }

    const responseBody = (await response.json()) as FhirSearchResult;

    if (!responseBody.entry) {
      throw jsonError(
        'HealthcareServiceEntryNotFoundError',
        HttpStatus.NotFound,
        `Es wurden keine Einträge mit Id='${hsId}' gefunden`
      );
    }
    if (responseBody.entry.length !== 1) {
      throw new Error('Die Suche nach dieser ID hat nicht exakt ein Ergebnis geliefert');
    }
    if (responseBody.entry[0].resource.resourceType !== 'HealthcareService') {
      throw new Error('Die Suche hat keinen HealthcareService zurück gegeben');
    }
    return responseBody.entry[0].resource as FhirHealthcareService;
  }

  /**
   * Get healthcareService for this id with associated Ressources
   * @param hsId ID of the healthcareService
   */
  public async getHealthcareServiceWithResources(hsId: string): Promise<FhirSearchResult> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling getHealthcareService.');
    }

    const token = await this.vzdTokenService.getToken();

    const params = new URLSearchParams();
    params.append('_id', hsId);
    params.append('organization.active', 'true');
    params.append('_include', 'HealthcareService:endpoint');
    params.append('_include', 'HealthcareService:location');
    params.append('_include', 'HealthcareService:organization');

    const url = `${this.vzdBase}/search/HealthcareService?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError(
        'GetHealthcareServiceWithResourcesError',
        response,
        'Suchen des Healthcareservices fehlgeschlagen'
      );
    }

    return (await response.json()) as FhirSearchResult;
  }

  /**
   * Get associated healthcareService
   * Get healthcareServices regarding the organization that belongs to the claimed device (the search result could also contain all other resources that are related with it. Please ensure that the searched resources are included at any time!)
   */
  public async getHealthcareServices(filter?: string): Promise<FhirSearchResult> {
    const token = await this.vzdTokenService.getToken();

    const organization = (await this.getOrganizationLocation()).organization;

    const paramsEndpointName = this.createDefaultSearchParams(organization);
    const paramsOrganizationName = this.createDefaultSearchParams(organization);
    const paramsHealthcareServiceName = this.createDefaultSearchParams(organization);

    if (filter) {
      paramsEndpointName.append('endpoint.name', filter);
      paramsOrganizationName.append('organization.name', filter);
      paramsHealthcareServiceName.append('name', filter);

      const urlEndpoint = `${this.vzdBase}/search/HealthcareService?${paramsEndpointName.toString()}`;
      const urlOrganization = `${this.vzdBase}/search/HealthcareService?${paramsOrganizationName.toString()}`;
      const urlHealthcareService = `${this.vzdBase}/search/HealthcareService?${paramsHealthcareServiceName.toString()}`;

      const responseEndpoint = await fetch(urlEndpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!responseEndpoint.ok || responseEndpoint.status >= 400) {
        throw await this.vzdError(
          'SearchHealthcareServiceByEndpointError',
          responseEndpoint,
          'Suchen des Healthcareservices fehlgeschlagen'
        );
      }
      const resultEndpoint = (await responseEndpoint.json()) as FhirSearchResult;

      const responseOrganization = await fetch(urlOrganization, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!responseOrganization.ok || responseOrganization.status >= 400) {
        throw await this.vzdError(
          'SearchHealthcareServiceByOrganizationError',
          responseOrganization,
          'Suchen des Healthcareservices fehlgeschlagen'
        );
      }
      const resultOrganization = (await responseOrganization.json()) as FhirSearchResult;

      const responseHealthcareService = await fetch(urlHealthcareService, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!responseHealthcareService.ok || responseHealthcareService.status >= 400) {
        throw await this.vzdError(
          'SearchHealthcareServiceByNameError',
          responseHealthcareService,
          'Suchen des Healthcareservices fehlgeschlagen'
        );
      }
      const resultHealthcareService = (await responseHealthcareService.json()) as FhirSearchResult;

      const merge = (
        a: FhirEntry[],
        b: FhirEntry[],
        predicate = (a: FhirEntry, b: FhirEntry): boolean => a.resource.id === b.resource.id
      ): FhirEntry[] => {
        const c = [...a];
        b.forEach(bItem => (c.some(cItem => predicate(bItem, cItem)) ? null : c.push(bItem)));
        return c;
      };

      resultEndpoint.entry = merge(resultEndpoint.entry ?? [], resultOrganization.entry ?? []);
      resultEndpoint.entry = merge(resultEndpoint.entry, resultHealthcareService.entry ?? []);
      resultEndpoint.total = resultEndpoint.entry.length;

      return resultEndpoint;
    } else {
      const urlHealthcareService = `${this.vzdBase}/search/HealthcareService?${paramsHealthcareServiceName.toString()}`;
      const response = await fetch(urlHealthcareService, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok || response.status >= 400) {
        throw await this.vzdError(
          'SearchHealthcareServiceError',
          response,
          'Suchen des Healthcareservices fehlgeschlagen'
        );
      }
      return (await response.json()) as FhirSearchResult;
    }
  }

  async addEndpointToHCS(hsId: string, endpointId: string): Promise<void> {
    const hcs: FhirHealthcareService = await this.getHealthcareService(hsId);

    hcs.endpoint ??= [];
    hcs.endpoint.push({ reference: `Endpoint/${endpointId}` } as FhirReference);

    await this.updateHealthcareService(hsId, hcs);
  }

  async removeEndpointFromHCS(hsId: string, endpointId: string): Promise<void> {
    const hcs: FhirHealthcareService = await this.getHealthcareService(hsId);

    hcs.endpoint = hcs.endpoint?.filter(fr => {
      return !fr.reference?.endsWith(`Endpoint/${endpointId}`);
    });

    await this.updateHealthcareService(hsId, hcs);
  }

  private createDefaultSearchParams(organization: FhirOrganization): URLSearchParams {
    const params = new URLSearchParams();
    if (organization.identifier?.[0].value) {
      params.append('organization.identifier', organization.identifier?.[0].value);
    }
    params.append('organization.active', 'true');
    params.append('_include', 'HealthcareService:endpoint');
    params.append('_include', 'HealthcareService:location');
    params.append('_include', 'HealthcareService:organization');
    return params;
  }

  /**
   * Create endpoint
   * @param hsId ID of the healthcareService
   * @param fhirEndpoint
   */
  public async createEndpoint(hsId: string, fhirEndpoint?: FhirEndpoint): Promise<FhirEndpoint> {
    if (!fhirEndpoint?.name || !fhirEndpoint?.address) {
      throw new Error('Required parameters name and address were null or undefined when calling createEndpoint.');
    }

    const token = await this.vzdTokenService.getToken();

    const url = `${this.vzdBase}/owner/Endpoint`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fhirEndpoint)
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('CreateEndpointError', response, 'Das Anlegen des Endpunktes ist fehlgeschlagen.');
    }

    if (!response.body) {
      throw new Error('Keine Antwort von der Schnittstelle');
    }

    const newEndpoint = (await response.json()) as FhirEndpoint;

    await this.addEndpointToHCS(hsId, newEndpoint.id!);

    return newEndpoint;
  }

  /**
   * Returns information about the requested endpoint
   * @param hsId ID of the healthcareService
   * @param endId ID of the endpoint
   */
  public async getEndpoint(hsId: string, endId: string): Promise<FhirEndpoint> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling getEndpoint.');
    }
    if (!endId) {
      throw new Error('Required parameter endId was null or undefined when calling getEndpoint.');
    }

    const token = await this.vzdTokenService.getToken();
    const params = new URLSearchParams();
    params.append('_id', endId);
    params.append('status', 'active');
    const url = `${this.vzdBase}/search/Endpoint?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('GetEndpointError', response, 'Suche nach Endpunkt fehlgeschlagen');
    }

    const responseBody = (await response.json()) as FhirSearchResult;

    if (!responseBody.entry) {
      throw new Error('Es wurden keine Einträge mit dieser ID gefunden');
    }
    if (responseBody.entry.length !== 1) {
      throw new Error('Die Suche nach dieser ID hat nicht exakt ein Ergebnis geliefert');
    }
    if (responseBody.entry[0].resource.resourceType !== 'Endpoint') {
      throw new Error('Die Suche hat keinen Endpunkt zurück gegeben');
    }
    return responseBody.entry[0].resource as FhirEndpoint;
  }

  /**
   * Get all endpoints for the healthcareService
   * Get all endpoints for healthcare-service. (the search result could also contain all other resources that are related with it. Please ensure that the searched resources are included at any time!)
   * @param hsId ID of the healthcareService
   */
  public async getEndpoints(hsId: string): Promise<FhirSearchResult> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling getEndpoints.');
    }

    const token = await this.vzdTokenService.getToken();
    const params = new URLSearchParams();
    params.append('_id', hsId);
    params.append('organization.active', 'true');
    params.append('_include', 'HealthcareService:endpoint');
    const url = `${this.vzdBase}/search/HealthcareService?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('GetEndpointsError', response, 'Suche nach Endpunkten fehlgeschlagen');
    }

    return (await response.json()) as FhirSearchResult;
  }

  /**
   * Remove the endpointVisibility of the endpoint of the healthcare service.
   * @param hsId ID of the healthcareService
   * @param endId ID of the endpoint
   */
  public async removeEndpointVisibilityOfHealthcareService(hsId: string, endId: string): Promise<FhirEndpoint> {
    if (!hsId) {
      throw new Error(
        'Required parameter hsId was null or undefined when calling removeEndpointVisibilityOfHealthcareService.'
      );
    }
    if (!endId) {
      throw new Error(
        'Required parameter endId was null or undefined when calling removeEndpointVisibilityOfHealthcareService.'
      );
    }

    const endpoint = await this.getEndpoint(hsId, endId);
    if (!endpoint) {
      throw new Error(`Endpoint with id ${endId} not found`);
    }

    endpoint.extension = endpoint.extension?.filter(
      ext => ext.url !== 'https://gematik.de/fhir/directory/StructureDefinition/EndpointVisibility'
    );

    return await this.updateEndpoint(hsId, endId, endpoint);
  }

  /**
   * Set the endpointVisibility of the endpoint of the healthcare service to hide-versichert
   * @param hsId ID of the healthcareService
   * @param endId ID of the endpoint
   */
  public async setEndpointVisibilityOfHealthcareService(hsId: string, endId: string): Promise<FhirEndpoint> {
    if (!hsId) {
      throw new Error(
        'Required parameter hsId was null or undefined when calling setEndpointVisibilityOfHealthcareService.'
      );
    }
    if (!endId) {
      throw new Error(
        'Required parameter endId was null or undefined when calling setEndpointVisibilityOfHealthcareService.'
      );
    }

    const endpoint = await this.getEndpoint(hsId, endId);
    if (!endpoint) {
      throw new Error(`Endpoint with id ${endId} not found`);
    }

    const extension = {
      url: 'https://gematik.de/fhir/directory/StructureDefinition/EndpointVisibility',
      valueCoding: {
        system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointVisibilityCS',
        code: 'hide-versicherte'
      }
    } as FhirExtension;
    if (endpoint.extension) {
      endpoint.extension.push(extension);
    } else {
      endpoint.extension = [extension];
    }
    return await this.updateEndpoint(hsId, endId, endpoint);
  }

  /**
   * Updates an endpoint
   * @param hsId ID of the healthcareService
   * @param endId ID of the endpoint
   * @param fhirEndpoint
   */
  public async updateEndpoint(hsId: string, endId: string, fhirEndpoint?: FhirEndpoint): Promise<FhirEndpoint> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling updateEndpoint.');
    }
    if (!endId) {
      throw new Error('Required parameter endId was null or undefined when calling updateEndpoint.');
    }
    if (!fhirEndpoint?.name || !fhirEndpoint?.address) {
      throw new Error('Required parameters name and address were null or undefined when calling updateEndpoint.');
    }

    const token = await this.vzdTokenService.getToken();

    const url = `${this.vzdBase}/owner/Endpoint/${endId}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fhirEndpoint)
    });

    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('UpdateEndpointError', response, 'Bearbeiten des Endpunktes fehlgeschlagen');
    }

    return (await response.json()) as FhirEndpoint;
  }

  /**
   * Deletes an endpoint
   * @param hsId ID of the healthcareService
   * @param endId ID of the endpoint
   */
  public async deleteEndpoint(hsId: string, endId: string): Promise<void> {
    if (!hsId) {
      throw new Error('Required parameter hsId was null or undefined when calling deleteEndpoint.');
    }
    if (!endId) {
      throw new Error('Required parameter endId was null or undefined when calling deleteEndpoint.');
    }

    await this.removeEndpointFromHCS(hsId, endId);

    const token = await this.vzdTokenService.getToken();

    const url = `${this.vzdBase}/owner/Endpoint/${endId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('DeleteEndpointError', response, 'Löschen des Endpunktes fehlgeschlagen');
    }
  }

  /**
   * Get organization from the selected device
   */
  public async getOrganization(): Promise<FhirOrganization> {
    const token = await this.vzdTokenService.getToken();
    const tid = decodeJwt(token)?.sub;

    if (!tid) {
      throw new Error('Es konnte keine Telematik ID gefunden werden');
    }

    const params = new URLSearchParams();
    params.append('identifier', tid);

    const url = `${this.vzdBase}/owner/Organization?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok || response.status >= 400) {
      throw await this.vzdError('GetOrganizationError', response, 'Suchen nach der Organisation ist fehlgeschlagen');
    }

    const responseBody = (await response.json()) as FhirSearchResult;

    if (!responseBody.entry) {
      throw new Error('Es konnte kein Eintrag für Organization oder Location gefunden werden');
    }

    if (responseBody.entry.length !== 1) {
      throw new Error('Es wurde nicht genau ein Eintrag gefunden!');
    }

    return responseBody.entry[0].resource as FhirOrganization;
  }

  /**
   * Baut aus einer fehlerhaften VZD-Response einen strukturierten Fehler, der den echten
   * HTTP-Status und (gekürzt) den Antwort-Body transportiert. Nur so reicht der Testtreiber
   * (handle-error.ts) den tatsächlichen Status durch, statt pauschal 500 zu melden – das ist
   * für die Diagnose von VZD-/FHIR-Fehlern essenziell (sonst Blindflug bei „500").
   */
  private async vzdError(errorName: string, response: Response, contextMessage: string): Promise<Error> {
    let body = '';
    try {
      body = (await response.text?.()) ?? '';
    } catch {
      body = '';
    }
    const snippet = body.trim().slice(0, 500);
    const message = snippet
      ? `${contextMessage} [VZD ${response.status}] ${snippet}`
      : `${contextMessage} [VZD ${response.status}]`;
    return jsonError(errorName, response.status, message);
  }

  /**
   * Führt fetch mit Retry-Logik aus.
   * - Retries bei Netzwerk-Fehlern und 5xx/429
   * - Keine Retries bei 4xx (außer 429)
   *
   * @param input RequestInfo (URL oder Request)
   * @param init RequestInit (Methoden, Header, Body, etc.)
   * @param retryDelaysMs Wartezeiten zwischen Retries in ms
   * @param shouldRetry Funktion zur Entscheidung, ob retry sinnvoll ist
   */
  private async retryFetch(
    input: RequestInfo | URL,
    init: RequestInit,
    retryDelaysMs: number[] = [1000, 3000, 5000],
    shouldRetry: (response: Response | undefined, error: unknown) => boolean = this.defaultShouldRetry
  ): Promise<Response> {
    let attempt = 0; // 0 = erster Versuch (ohne Wartezeit)
    const maxRetries = retryDelaysMs.length;

    while (true) {
      try {
        const response = await fetch(input, init);

        if (response.ok) {
          return response;
        }

        // Prüfen, ob wir wiederholen sollen
        if (!shouldRetry(response, undefined) || attempt >= maxRetries) {
          // Kein weiterer Retry -> entweder letzter Versuch oder nicht retry-fähiger Status
          return response;
        }

        // Nächste Wartezeit anwenden
        const delay = retryDelaysMs[attempt];
        await sleep(delay);
        attempt++;
      } catch (error) {
        // Netzwerk-/Fetch-Fehler (z.B. CORS, DNS, Timeout via AbortController etc.)
        if (!shouldRetry(undefined, error) || attempt >= maxRetries) {
          // Letzter Versuch fehlgeschlagen -> Fehler weiterwerfen
          throw error;
        }

        const delay = retryDelaysMs[attempt];
        await sleep(delay);
        attempt++;
      }
    }
  }

  /**
   * Default-Strategie für Retrying:
   * - Retry bei 5xx und 429 (Rate Limiting)
   * - Kein Retry bei 4xx (Client-Fehler), außer 429
   * - Retry bei Netzwerk-/Fetch-Fehlern (error !== undefined)
   */
  private defaultShouldRetry(response: Response | undefined, error: unknown): boolean {
    if (error) return true; // Netzwerk-/Fetch-Fehler -> retry
    if (!response) return true;

    const { status } = response;

    // 2xx: kein Retry nötig (wird vorher abgefangen)
    if (status >= 200 && status < 300) return false;

    // 429 (Too Many Requests) -> retry
    if (status === 429) return true;

    // 5xx -> retry
    if (status >= 500 && status <= 599) return true;

    // Sonst: kein Retry (z.B. 400/401/403/404 etc.)
    return false;
  }
}
